import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL_SAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_grounded-wellness-3/artifacts/etbi1b2r_Untitled%20design.png";
const LOGO_URL_PURPLE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_grounded-wellness-3/artifacts/lr6iybd4_Untitled%20design.png";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/services", label: "Yoga Therapy", testid: "nav-link-services" },
  { to: "/pricing", label: "Investment", testid: "nav-link-pricing" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isTantric = location.pathname === "/intimacy-tantra";
  const logoUrl = isTantric ? LOGO_URL_PURPLE : LOGO_URL_SAGE;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? isTantric
            ? "bg-[#17002B]/85 backdrop-blur-xl border-b border-[#C5A059]/25"
            : "bg-[#FDFCFB]/85 backdrop-blur-xl border-b border-[#E8E5DF]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={logoUrl}
            alt="Shakti Yogini Sri Yantra logo"
            className="w-11 h-11 md:w-12 md:h-12 rounded-full object-contain transition-transform duration-500 group-hover:rotate-12"
            data-testid="nav-logo-image"
          />
          <span
            className={`font-serif text-2xl md:text-[1.6rem] tracking-tight leading-none ${
              isTantric ? "text-[#C5A059]" : "text-[#2D3A2B]"
            }`}
          >
            Shakti Yogini
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isTantric
                    ? isActive
                      ? "text-[#F5E9D5]"
                      : "text-[#C5A059] hover:text-[#F5E9D5]"
                    : isActive
                    ? "text-[#2D3A2B]"
                    : "text-[#4A5D46] hover:text-[#2D3A2B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-cta-book">
            <Button
              className={`rounded-full px-6 py-2 h-auto transition-colors ${
                isTantric
                  ? "bg-[#D8A1A1] hover:bg-[#C88F8F] text-[#4B0082]"
                  : "bg-[#8A9A83] hover:bg-[#7B8E76] text-white"
              }`}
            >
              Book a Consultation
            </Button>
          </Link>
        </nav>

        <button
          className={`md:hidden ${isTantric ? "text-[#C5A059]" : "text-[#2D3A2B]"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-[#FDFCFB] border-t border-[#E8E5DF]"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className="text-base text-[#2D3A2B] py-2"
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-cta-book-mobile">
              <Button
                className={`w-full rounded-full transition-colors ${
                  isTantric
                    ? "bg-[#D8A1A1] hover:bg-[#C88F8F] text-[#4B0082]"
                    : "bg-[#8A9A83] hover:bg-[#7B8E76] text-white"
                }`}
              >
                Book a Consultation
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
